import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { useSelector } from "react-redux";
import { authGet, authPost, authPut, authDelete } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1100, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  tabPanel: { paddingTop: theme.spacing(3) },
  addRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  vendorTable: { maxWidth: 600, width: "100%" },
  uploadForm: { display: "flex", flexDirection: "column", gap: theme.spacing(2), maxWidth: 700 },
  resultOk: { backgroundColor: "#f5fff5" },
  resultWarn: { backgroundColor: "#fffbe6" },
  resultErr: { backgroundColor: "#fff5f5" },
  statusCell: { fontSize: "0.8rem" },
  errorText: { color: "#d32f2f" },
  warnText: { color: "#ed6c02" },
  okText: { color: "#2e7d32" },
  monoArea: { fontFamily: "monospace" },
}));

export function VendorsPage() {
  const classes = useStyles();
  const siteId = useSelector((state) => state.scopes.site_id);

  const [tab, setTab] = useState(0);

  // Vendors tab
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // List upload tab
  const [uploadVendorId, setUploadVendorId] = useState("");
  const [uploadExp, setUploadExp] = useState("");
  const [uploadReplace, setUploadReplace] = useState(false);
  const [uploadText, setUploadText] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewResults, setPreviewResults] = useState(null);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    fetchVendors();
  }, [siteId]);

  async function fetchVendors() {
    setLoadingVendors(true);
    const { data, error } = await authGet(["/vendors", { site: siteId }]);
    setLoadingVendors(false);
    if (error) { showSnack(error?.message || "Failed to load vendors"); return; }
    setVendors(data || []);
  }

  function showSnack(msg) {
    setSnack({ open: true, msg });
  }

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    const { data, error } = await authPost("/vendors", { vendor: name, site_id: siteId });
    if (error) { showSnack(error?.message || "Failed to add vendor"); return; }
    setVendors((prev) => [...prev, data].sort((a, b) => a.vendor.localeCompare(b.vendor)));
    setNewName("");
    showSnack("Vendor added");
  }

  async function handleSaveEdit() {
    const name = editName.trim();
    if (!name) return;
    const { data, error } = await authPut(`/vendors/${editId}`, { vendor: name });
    if (error) { showSnack(error?.message || "Failed to update vendor"); return; }
    setVendors((prev) =>
      prev.map((v) => (v.vendor_id === editId ? data : v)).sort((a, b) => a.vendor.localeCompare(b.vendor))
    );
    setEditId(null);
    showSnack("Vendor updated");
  }

  async function handleDelete() {
    const { error } = await authDelete(`/vendors/${confirmDeleteId}`);
    if (error) { showSnack(error?.message || "Failed to delete vendor"); return; }
    setVendors((prev) => prev.filter((v) => v.vendor_id !== confirmDeleteId));
    setConfirmDeleteId(null);
    showSnack("Vendor deleted");
  }

  async function handlePreview() {
    if (!uploadText.trim()) return;
    setPreviewing(true);
    const { data, error } = await authPost("/vendor-list-preview", { list: uploadText, site_id: siteId });
    setPreviewing(false);
    if (error) { showSnack(error?.message || "Preview failed"); return; }
    setPreviewResults(data);
  }

  async function handleSubmit() {
    if (!uploadVendorId) { showSnack("Select a vendor"); return; }
    if (!uploadExp) { showSnack("Set an expiry date"); return; }
    if (!uploadText.trim()) { showSnack("Paste a list first"); return; }
    setSubmitting(true);
    const { data, error } = await authPost("/vendor-list-submit", {
      list: uploadText,
      vendor_id: uploadVendorId,
      exp: uploadExp,
      replace: uploadReplace,
      site_id: siteId,
    });
    setSubmitting(false);
    if (error) { showSnack(error?.message || "Submit failed"); return; }
    showSnack(`Updated ${data.updated} item${data.updated !== 1 ? "s" : ""}`);
    setPreviewResults(data.results);
  }

  const deleteTarget = vendors.find((v) => v.vendor_id === confirmDeleteId);
  const validCount = previewResults ? previewResults.filter((r) => !r.hasError).length : 0;

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>Vendors</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
        <Tab label="Vendor List" />
        <Tab label="List Upload" />
      </Tabs>

      {tab === 0 && (
        <Box className={classes.tabPanel}>
          <Box className={classes.addRow}>
            <TextField
              size="small"
              variant="outlined"
              label="Vendor name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <Button variant="contained" color="primary" onClick={handleAdd} disabled={!newName.trim()}>
              Add
            </Button>
          </Box>

          {loadingVendors ? (
            <CircularProgress size={24} />
          ) : (
            <TableContainer component={Paper} className={classes.vendorTable}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: 60 }}>ID</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell align="center" style={{ width: 60 }}>Edit</TableCell>
                    <TableCell align="center" style={{ width: 60 }}>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors.map((v) => (
                    <TableRow key={v.vendor_id} hover>
                      <TableCell>{v.vendor_id}</TableCell>
                      <TableCell>
                        {editId === v.vendor_id ? (
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(); if (e.key === "Escape") setEditId(null); }}
                            autoFocus
                          />
                        ) : (
                          v.vendor
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId === v.vendor_id ? (
                          <>
                            <IconButton size="small" onClick={handleSaveEdit}><CheckIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => setEditId(null)}><CloseIcon fontSize="small" /></IconButton>
                          </>
                        ) : (
                          <IconButton size="small" onClick={() => { setEditId(v.vendor_id); setEditName(v.vendor); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId !== v.vendor_id && (
                          <IconButton size="small" onClick={() => setConfirmDeleteId(v.vendor_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {vendors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ color: "#999", padding: 24 }}>
                        No vendors yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box className={classes.tabPanel}>
          <Box className={classes.uploadForm}>
            <FormControl variant="outlined" size="small" style={{ maxWidth: 300 }}>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={uploadVendorId}
                onChange={(e) => setUploadVendorId(e.target.value)}
                label="Vendor"
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                }}
              >
                {vendors.map((v) => (
                  <MenuItem key={v.vendor_id} value={v.vendor_id}>{v.vendor}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              type="date"
              label="Expiry Date"
              variant="outlined"
              size="small"
              value={uploadExp}
              onChange={(e) => setUploadExp(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ maxWidth: 200 }}
            />

            <FormControlLabel
              control={<Checkbox checked={uploadReplace} onChange={(e) => setUploadReplace(e.target.checked)} color="primary" />}
              label="Replace existing specials for this vendor (clears all current specials for this vendor before applying the list below)"
            />

            <Box>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Paste tab-separated data (3 columns): <strong>Model No</strong>, <strong>MSRP</strong>, <strong>Discount %</strong>
              </Typography>
              <TextField
                multiline
                rows={12}
                fullWidth
                variant="outlined"
                placeholder={"MODEL-REF\t999.00\t15"}
                value={uploadText}
                onChange={(e) => { setUploadText(e.target.value); setPreviewResults(null); }}
                inputProps={{ className: classes.monoArea }}
              />
            </Box>

            <Box display="flex" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePreview}
                disabled={previewing || !uploadText.trim()}
              >
                {previewing ? "Previewing…" : "Preview"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting || !previewResults || validCount === 0}
              >
                {submitting ? "Submitting…" : `Submit${previewResults ? ` (${validCount} items)` : ""}`}
              </Button>
            </Box>

            {previewResults && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview — {validCount} of {previewResults.length} valid
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: 40 }}>#</TableCell>
                        <TableCell>Model No</TableCell>
                        <TableCell>MSRP</TableCell>
                        <TableCell>Disc %</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewResults.map((r) => (
                        <TableRow
                          key={r.lineNo}
                          className={r.hasError ? classes.resultErr : r.warnings?.length ? classes.resultWarn : classes.resultOk}
                        >
                          <TableCell>{r.lineNo}</TableCell>
                          <TableCell>{r.model_no}</TableCell>
                          <TableCell>{r.msrp}</TableCell>
                          <TableCell>{r.discount}</TableCell>
                          <TableCell className={classes.statusCell}>
                            {r.errors.map((e, i) => <div key={i} className={classes.errorText}>{e}</div>)}
                            {r.warnings.map((w, i) => <div key={i} className={classes.warnText}>{w}</div>)}
                            {!r.hasError && !r.warnings?.length && <span className={classes.okText}>OK</span>}
                            {!r.hasError && r.warnings?.length > 0 && <span className={classes.warnText}> (will update)</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete Vendor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.vendor}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}